import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { ChevronLeft, ChevronRight, Loader2, Save, X, Eye, AlertTriangle } from "lucide-react";
import { useImpersonation } from "@/hooks/useImpersonation";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLanguage } from "@/contexts/LanguageContext";

// Import step components
import ChiefComplaintStep from "./steps/ChiefComplaintStep";
import SymptomDetailsStep from "./steps/SymptomDetailsStep";
import VitalsRiskStep from "./steps/VitalsRiskStep";
import RedFlagsStep from "./steps/RedFlagsStep";
import ReviewStep from "./steps/ReviewStep";
import { LanguageSelector } from "./LanguageSelector";

export interface TriageData {
  chief_complaint: string;
  symptoms: string[];
  pain_severity: number;
  symptom_onset: string;
  symptom_duration: string;
  location: string;
  vitals: {
    temperature?: string;
    blood_pressure?: string;
    heart_rate?: string;
  };
  medical_history: string[];
  medications: string[];
  allergies: string[];
  recent_visits: string[];
  emergency_symptoms: string[];
  pregnancy?: boolean;
  risk_factors: string[];
}

const STEPS = [
  { id: 1, title: "Chief Complaint", component: ChiefComplaintStep, description: "Tell us what's bothering you the most right now" },
  { id: 2, title: "Symptom Details", component: SymptomDetailsStep, description: "Describe your symptoms, severity, and when they started" },
  { id: 3, title: "Vitals & Risk Factors", component: VitalsRiskStep, description: "Share any vitals and medical history that may be relevant" },
  { id: 4, title: "Emergency Symptoms", component: RedFlagsStep, description: "Check for any warning signs that need immediate attention" },
  { id: 5, title: "Review", component: ReviewStep, description: "Review your answers and get your personalized recommendation" },
];

interface RiskFlags {
  allergies?: string[];
  medications?: string[];
  conditions?: string[];
}

// Map profile conditions to triage medical history keys
const CONDITION_MAP: Record<string, string> = {
  "Diabetes": "diabetes",
  "High Blood Pressure": "hypertension",
  "Heart Disease": "heart_disease",
  "Asthma": "asthma",
  "Arthritis": "arthritis",
  "Depression/Anxiety": "mental_health_condition",
  "Thyroid Disorder": "thyroid_disorder",
  "Cancer History": "cancer"
};

const TriageWizard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeSessionId = searchParams.get("resume");
  const { toast } = useToast();
  const { endImpersonation } = useImpersonation();
  const { t } = useLanguage();
  
  // Check for patient impersonation mode
  const isPatientImpersonation = searchParams.get('impersonate') === 'patient';
  const impersonationSessionId = searchParams.get('sid');
  const patientId = searchParams.get('pid');
  
  const [currentStep, setCurrentStep] = useState(1);
  const [sessionId, setSessionId] = useState<string | null>(resumeSessionId);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sessionInitialized, setSessionInitialized] = useState(!!resumeSessionId);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [resumeLoaded, setResumeLoaded] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  
  const [triageData, setTriageData] = useState<TriageData>({
    chief_complaint: "",
    symptoms: [],
    pain_severity: 0,
    symptom_onset: "",
    symptom_duration: "",
    location: "",
    vitals: {},
    medical_history: [],
    medications: [],
    allergies: [],
    recent_visits: [],
    emergency_symptoms: [],
    risk_factors: []
  });
  
  // Check if user has unsaved progress
  const hasUnsavedProgress = useCallback(() => {
    return sessionInitialized && (
      triageData.chief_complaint.length > 0 ||
      triageData.symptoms.length > 0 ||
      triageData.emergency_symptoms.length > 0
    );
  }, [sessionInitialized, triageData]);
  
  // Handle browser back button and tab close
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedProgress()) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedProgress]);
  
  // Safe navigation with confirmation
  const safeNavigate = useCallback((path: string) => {
    if (hasUnsavedProgress()) {
      setPendingNavigation(path);
      setShowExitDialog(true);
    } else {
      navigate(path);
    }
  }, [hasUnsavedProgress, navigate]);
  
  const confirmExit = () => {
    setShowExitDialog(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };
  
  const saveAndExit = async () => {
    if (sessionId) {
      await saveCurrentStep();
      toast({
        title: "Progress saved",
        description: "You can continue this assessment from your dashboard.",
      });
    }
    setShowExitDialog(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  // Check authentication and load profile/resume data
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    // Redirect to login if not authenticated
    if (!user) {
      navigate("/login");
      return;
    }

    // Load resume data if resuming a session
    const loadResumeData = async () => {
      if (!resumeSessionId || resumeLoaded) return;
      
      try {
        setLoading(true);
        
        // Verify session exists and belongs to user
        const { data: session, error: sessionError } = await supabase
          .from("triage_sessions")
          .select("id, completed_at")
          .eq("id", resumeSessionId)
          .maybeSingle();

        if (sessionError || !session) {
          toast({
            title: "Session not found",
            description: "Starting a new assessment instead.",
            variant: "destructive",
          });
          setSessionId(null);
          setSessionInitialized(false);
          setResumeLoaded(true);
          return;
        }

        if (session.completed_at) {
          toast({
            title: "Assessment already completed",
            description: "Starting a new assessment instead.",
          });
          setSessionId(null);
          setSessionInitialized(false);
          setResumeLoaded(true);
          return;
        }

        // Load existing answers
        const { data: answers, error: answersError } = await supabase
          .from("triage_answers")
          .select("question_key, value")
          .eq("session_id", resumeSessionId)
          .order("created_at", { ascending: false })
          .limit(1);

        if (!answersError && answers && answers.length > 0) {
          const savedData = answers[0].value as unknown as TriageData;
          if (savedData && typeof savedData === 'object') {
            setTriageData(prev => ({
              ...prev,
              ...savedData
            }));
            
            // Determine which step to resume from based on saved data
            let resumeStep = 1;
            if (savedData.emergency_symptoms?.length >= 0 && savedData.medical_history) resumeStep = 5;
            else if (savedData.medical_history?.length >= 0) resumeStep = 4;
            else if (savedData.symptoms?.length > 0) resumeStep = 3;
            else if (savedData.chief_complaint) resumeStep = 2;
            
            setCurrentStep(resumeStep);
          }
        }

        toast({
          title: "Assessment resumed",
          description: "Continue where you left off.",
        });
        
        setResumeLoaded(true);
        setProfileLoaded(true); // Skip profile loading when resuming
      } catch (error) {
        console.error("Error loading resume data:", error);
      } finally {
        setLoading(false);
      }
    };

    // Load profile data to pre-fill medical history (only for new sessions)
    const loadProfileData = async () => {
      if (profileLoaded || resumeSessionId) return;
      
      try {
        const { data: patient, error } = await supabase
          .from("patients")
          .select("risk_flags")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error) {
          console.error("Error loading profile:", error);
          return;
        }

        if (patient?.risk_flags) {
          const riskFlags = patient.risk_flags as RiskFlags;
          
          // Map profile conditions to triage medical history format
          const mappedConditions = (riskFlags.conditions || [])
            .map(c => CONDITION_MAP[c])
            .filter(Boolean);

          setTriageData(prev => ({
            ...prev,
            allergies: riskFlags.allergies || [],
            medications: riskFlags.medications || [],
            medical_history: mappedConditions
          }));
        }
        
        setProfileLoaded(true);
      } catch (error) {
        console.error("Error loading profile data:", error);
      }
    };

    if (resumeSessionId) {
      loadResumeData();
    } else {
      loadProfileData();
    }
  }, [user, authLoading, navigate, profileLoaded, resumeSessionId, resumeLoaded, toast]);

  // Initialize session when user starts answering questions
  const ensureSessionInitialized = async () => {
    if (sessionInitialized || sessionId) return;

    try {
      setLoading(true);
      setSessionError(null);
      const { data, error } = await supabase.functions.invoke("triage-start");
      
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      
      setSessionId(data.session_id);
      setSessionInitialized(true);
      
      toast({
        title: "Assessment Started",
        description: "Your responses are now being saved.",
      });
    } catch (error: any) {
      const errorMessage = error.message || "Failed to start assessment";
      setSessionError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Modified data update handler to initialize session
  const updateTriageData = async (newData: TriageData) => {
    setTriageData(newData);
    
    // Initialize session when user first starts answering
    if (!sessionInitialized && !sessionId) {
      await ensureSessionInitialized();
    }
  };

  // Auto-save answers when data changes
  useEffect(() => {
    if (sessionId && Object.keys(triageData).some(key => {
      const value = triageData[key as keyof TriageData];
      return Array.isArray(value) ? value.length > 0 : value !== "" && value !== 0;
    })) {
      saveCurrentStep();
    }
  }, [triageData, sessionId]);

  const saveCurrentStep = async () => {
    if (!sessionId) return;

    try {
      // Save the current step's data
      const stepKey = STEPS[currentStep - 1].title.toLowerCase().replace(' ', '_');
      await supabase.functions.invoke("triage-save-answer", {
        body: {
          session_id: sessionId,
          question_key: stepKey,
          value: triageData
        }
      });
    } catch (error) {
      console.error("Auto-save error:", error);
    }
  };

  const nextStep = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return triageData.chief_complaint.length > 0;
      case 2:
        return triageData.symptoms.length > 0;
      case 3:
        return true; // Vitals are optional
      case 4:
        return true; // Emergency symptoms can be none
      case 5:
        return true; // Review step
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    if (!sessionId) return;

    try {
      setSubmitting(true);
      
      // Complete the triage assessment
      const { data, error } = await supabase.functions.invoke("triage-complete", {
        body: { session_id: sessionId }
      });
      
      if (error) throw error;
      
      // Navigate to results page with the recommendation
      navigate("/results", { 
        state: { 
          triageResult: data,
          sessionId: sessionId 
        }
      });
      
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to complete assessment",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Show loading screen only while auth is loading
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center p-8">
            <Loader2 className="w-8 h-8 animate-spin mb-4" />
            <p className="text-muted-foreground">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const CurrentStepComponent = STEPS[currentStep - 1].component;
  const progress = (currentStep / STEPS.length) * 100;

  const handleEndPatientImpersonation = async () => {
    if (impersonationSessionId) {
      await endImpersonation(impersonationSessionId);
    }
    navigate('/admin/patients', { replace: true });
    toast({
      title: "Session Ended",
      description: "Patient impersonation session has ended.",
    });
  };

  return (
    <>
      {/* Exit Confirmation Dialog */}
      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-amber-500/10">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              </div>
              <AlertDialogTitle>{t.wizard.exitDialog.title}</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="pt-2">
              {t.wizard.exitDialog.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={() => setShowExitDialog(false)}>
              {t.wizard.exitDialog.continueAssessment}
            </AlertDialogCancel>
            <Button
              variant="outline"
              onClick={confirmExit}
              className="text-destructive hover:text-destructive"
            >
              {t.wizard.exitDialog.leaveWithoutSaving}
            </Button>
            {sessionId && (
              <AlertDialogAction onClick={saveAndExit}>
                <Save className="w-4 h-4 mr-2" />
                {t.wizard.exitDialog.saveAndExit}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="min-h-screen bg-background py-8">
      {/* Patient Impersonation Banner */}
      {isPatientImpersonation && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-amber-950 px-4 py-2 shadow-lg">
          <div className="container mx-auto max-w-2xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Eye className="h-5 w-5" />
              <span className="font-medium">
                Viewing as Patient - All actions are logged
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleEndPatientImpersonation}
              className="text-amber-950 hover:bg-amber-600/30"
            >
              <X className="h-4 w-4 mr-1" />
              End Session
            </Button>
          </div>
        </div>
      )}
      
      <div className={`container mx-auto px-4 max-w-2xl ${isPatientImpersonation ? 'pt-12' : ''}`}>
        {/* Progress Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-foreground">{t.wizard.header.title}</h1>
            <div className="flex items-center gap-4">
              <LanguageSelector />
              <div className="flex items-center gap-2">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                <span className="text-sm text-muted-foreground">
                  {t.wizard.header.stepOf.replace('{current}', String(currentStep)).replace('{total}', String(STEPS.length))}
                </span>
              </div>
            </div>
          </div>
          
          {/* Visual Step Indicator */}
          <TooltipProvider delayDuration={200}>
            <div className="flex items-center justify-between mb-4">
              {STEPS.map((step, index) => {
                const stepNumber = index + 1;
                const isCompleted = stepNumber < currentStep;
                const isCurrent = stepNumber === currentStep;
                const isUpcoming = stepNumber > currentStep;
                const isClickable = isCompleted;
                
                const handleStepClick = () => {
                  if (isClickable) {
                    setCurrentStep(stepNumber);
                  }
                };
                
                return (
                  <React.Fragment key={step.id}>
                    {/* Step Circle with Tooltip */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div 
                          className={`flex flex-col items-center ${isClickable ? 'cursor-pointer' : isCurrent ? 'cursor-default' : 'cursor-not-allowed'}`}
                          onClick={handleStepClick}
                          role={isClickable ? "button" : undefined}
                          tabIndex={isClickable ? 0 : undefined}
                          onKeyDown={(e) => {
                            if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                              e.preventDefault();
                              handleStepClick();
                            }
                          }}
                        >
                          <div 
                            className={`
                              w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold
                              transition-all duration-300
                              ${isCompleted 
                                ? 'bg-primary text-primary-foreground scale-100 hover:scale-110 hover:ring-4 hover:ring-primary/20' 
                                : isCurrent 
                                  ? 'bg-primary text-primary-foreground ring-4 ring-primary/20 scale-110 animate-pulse' 
                                  : 'bg-muted text-muted-foreground scale-90 opacity-60'
                              }
                            `}
                          >
                            {isCompleted ? (
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              stepNumber
                            )}
                          </div>
                          <span 
                            className={`
                              text-xs mt-2 text-center max-w-[60px] leading-tight hidden sm:block
                              transition-colors duration-300
                              ${isCurrent ? 'text-primary font-medium' : isCompleted ? 'text-muted-foreground hover:text-primary' : 'text-muted-foreground/60'}
                            `}
                          >
                            {step.title}
                          </span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[200px] text-center">
                        <p className="font-medium text-sm">{step.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">{step.description}</p>
                        {isCompleted && (
                          <p className="text-xs text-primary mt-1">✓ Completed · Click to edit</p>
                        )}
                        {isCurrent && (
                          <p className="text-xs text-primary mt-1">● Current step</p>
                        )}
                        {isUpcoming && (
                          <p className="text-xs text-muted-foreground mt-1">○ Upcoming</p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                    
                    {/* Connector Line */}
                    {index < STEPS.length - 1 && (
                      <div className="flex-1 mx-2 h-1 rounded-full bg-muted overflow-hidden">
                        <div 
                          className={`
                            h-full bg-primary transition-all duration-500 ease-out
                            ${isCompleted ? 'w-full' : 'w-0'}
                          `}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </TooltipProvider>
          
          <Progress value={progress} className="w-full" />
          <div className="flex justify-between items-center mt-2">
            <p className="text-sm text-muted-foreground">
              {STEPS[currentStep - 1].title}
              {loading && " • Saving your responses..."}
            </p>
            <p className="text-xs text-muted-foreground">
              ~{Math.max(1, STEPS.length - currentStep + 1)} min remaining
            </p>
          </div>
        </div>

        {/* Current Step */}
        <Card className="health-card mb-6">
          <CardHeader>
            <CardTitle>{STEPS[currentStep - 1].title}</CardTitle>
          </CardHeader>
          <CardContent>
            <CurrentStepComponent
              data={triageData}
              updateData={updateTriageData}
              prefilled={profileLoaded && currentStep === 3}
            />
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 1}
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              {t.wizard.navigation.previous}
            </Button>
            
            {sessionId ? (
              <Button
                variant="ghost"
                onClick={() => safeNavigate("/")}
                className="text-muted-foreground"
              >
                <Save className="w-4 h-4 mr-2" />
                {t.wizard.exitDialog.saveAndExit}
              </Button>
            ) : (
              <Button
                variant="ghost"
                onClick={() => safeNavigate("/")}
                className="text-muted-foreground"
              >
                <X className="w-4 h-4 mr-2" />
                {t.wizard.navigation.exit}
              </Button>
            )}
          </div>

          {currentStep === STEPS.length ? (
            <div className="flex flex-col items-end gap-2">
              {sessionError && (
                <p className="text-sm text-destructive">{sessionError}</p>
              )}
              <Button 
                onClick={handleSubmit}
                disabled={!canProceed() || submitting || !sessionId}
              >
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {t.wizard.navigation.completeAssessment}
              </Button>
            </div>
          ) : (
            <Button
              onClick={nextStep}
              disabled={!canProceed()}
            >
              {t.wizard.navigation.next}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
      </div>
    </>
  );
};

export default TriageWizard;